import { ApiResponse } from '@shared/types';
import {
  AccountingPeriodRecord,
  SaveAccountingPeriodRequest,
} from '../types/accounting';
import { supabase } from './supabase';

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  return Number(value || 0);
};

const mapAccountingPeriod = (
  value: Record<string, unknown>,
): AccountingPeriodRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  outlet_id: (value.outlet_id as string | null) ?? null,
  name: String(value.name || ''),
  period_start: String(value.period_start || ''),
  period_end: String(value.period_end || ''),
  status: value.status as AccountingPeriodRecord['status'],
  closed_at: (value.closed_at as string | null) ?? null,
  closed_by: (value.closed_by as string | null) ?? null,
  notes: (value.notes as string | null) ?? null,
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

export const fetchAccountingPeriods = async (
  companyId: number,
): Promise<ApiResponse<AccountingPeriodRecord[]>> => {
  try {
    const { data, error } = await supabase
      .from('accounting_periods')
      .select('*')
      .eq('company_id', companyId)
      .order('period_start', { ascending: false });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) => mapAccountingPeriod(item)),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch accounting periods',
      data: [],
    };
  }
};

export const saveAccountingPeriod = async (
  input: SaveAccountingPeriodRequest,
): Promise<ApiResponse<AccountingPeriodRecord>> => {
  const payload = {
    company_id: input.companyId,
    outlet_id: input.outletId ?? null,
    name: input.name.trim(),
    period_start: input.periodStart,
    period_end: input.periodEnd,
    notes: input.notes?.trim() || null,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('accounting_periods')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('id', input.id)
        .eq('status', 'open')
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapAccountingPeriod(data) };
    }

    const { data, error } = await supabase
      .from('accounting_periods')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapAccountingPeriod(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save accounting period',
      data: undefined,
    };
  }
};

export interface CloseAccountingPeriodResult {
  leavesCoverageGap: boolean;
  gapStartDate: string | null;
}

export const closeAccountingPeriod = async (
  periodId: string,
  closedBy?: string | null,
): Promise<ApiResponse<CloseAccountingPeriodResult>> => {
  try {
    const { data, error } = await supabase.rpc('close_accounting_period', {
      p_period_id: periodId,
      p_closed_by: closedBy ?? null,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const row = Array.isArray(data) ? data[0] : data;

    return {
      success: true,
      data: {
        leavesCoverageGap: Boolean(row?.leaves_coverage_gap),
        gapStartDate: (row?.gap_start_date as string | null) ?? null,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to close accounting period',
      data: undefined,
    };
  }
};

export const reopenAccountingPeriod = async (
  periodId: string,
): Promise<ApiResponse<AccountingPeriodRecord>> => {
  try {
    const { data, error } = await supabase.rpc('reopen_accounting_period', {
      p_period_id: periodId,
    });

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const row = Array.isArray(data) ? data[0] : data;

    return { success: true, data: mapAccountingPeriod(row) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to reopen accounting period',
      data: undefined,
    };
  }
};
